/**
 * Document Management Module - Document Details Page
 * 
 * Comprehensive view of a document including:
 * - Full metadata
 * - Version history
 * - Approval workflow timeline
 * - Actions based on user role
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import {
  FaFilePdf,
  FaFileWord,
  FaDownload,
  FaHistory,
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaBan,
  FaExclamationCircle,
  FaArrowLeft,
  FaUpload,
} from 'react-icons/fa';
import { MdOutlineDescription } from 'react-icons/md';
import { Document, DocumentVersion, ApprovalRecord, DocumentStatus } from './types';
import documentService from './documentService';
import { getUserInfo } from '../../services/userServicer';
import './DocumentManagement.css';

const DocumentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'details' | 'versions' | 'approval'>('details');
  const [processing, setProcessing] = useState(false);
  const [folderName, setFolderName] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Approval workflow modals
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewApproved, setReviewApproved] = useState(true);
  const [reviewComments, setReviewComments] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveComments, setApproveComments] = useState('');
  const [approveModalAction, setApproveModalAction] = useState<'approve' | 'publish'>('approve');
  const [showUploadVersionModal, setShowUploadVersionModal] = useState(false);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionChangeDescription, setVersionChangeDescription] = useState('');

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await getUserInfo();
        setCurrentUserId(user?.id || null);
      } catch (error) {
        console.error('Failed to load user info:', error);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (id) {
      loadDocument();
    }
  }, [id]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const doc = await documentService.getDocumentById(id!);
      if (!doc) {
        toast.error('Document not found');
        navigate('/documents');
        return;
      }
      setDocument(doc);

      // Load folder name if folderId exists
      if (doc.metadata.folderId) {
        try {
          const folders = await documentService.getFolders();
          const folder = folders.find(f => f.id === doc.metadata.folderId);
          if (folder) {
            setFolderName(folder.path);
          }
        } catch (error) {
          console.error('Failed to load folder name:', error);
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!document) return;
    setProcessing(true);
    try {
      await documentService.submitForReview(document.metadata.id);
      toast.success('Document submitted for review');
      await loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit for review');
    } finally {
      setProcessing(false);
    }
  };

  const handleReview = async (approved: boolean, comments?: string) => {
    if (!document) return;
    setProcessing(true);
    try {
      await documentService.reviewDocument(document.metadata.id, approved, comments);
      toast.success(approved ? 'Document reviewed and sent for approval' : 'Document returned for revision');
      setShowReviewModal(false);
      setReviewComments('');
      await loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Failed to review document');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewModal = (approved: boolean) => {
    setReviewApproved(approved);
    setReviewComments('');
    setShowReviewModal(true);
  };

  const handleApprove = async (comments?: string) => {
    if (!document) return;
    setProcessing(true);
    try {
      // Note: Backend approve endpoint may not support comments directly
      // If needed, we can extend the API later
      await documentService.approveDocument(document.metadata.id);
      toast.success('Document approved');
      setShowApproveModal(false);
      setApproveComments('');
      await loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve document');
    } finally {
      setProcessing(false);
    }
  };

  const openApproveModal = (action: 'approve' | 'publish' = 'approve') => {
    setApproveComments('');
    setApproveModalAction(action);
    setShowApproveModal(true);
  };

  const handlePublish = async () => {
    if (!document) return;
    setProcessing(true);
    try {
      await documentService.publishDocument(document.metadata.id);
      toast.success('Document published');
      await loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Failed to publish document');
    } finally {
      setProcessing(false);
    }
  };

  const handleUploadVersion = async () => {
    if (!document || !versionFile) return;
    setProcessing(true);
    try {
      await documentService.uploadDocumentVersion(
        document.metadata.id,
        versionFile,
        versionChangeDescription || undefined
      );
      toast.success('New version uploaded successfully. Document status has been reset to Draft for review.');
      setShowUploadVersionModal(false);
      setVersionFile(null);
      setVersionChangeDescription('');
      await loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload new version');
    } finally {
      setProcessing(false);
    }
  };

  const openUploadVersionModal = () => {
    setVersionFile(null);
    setVersionChangeDescription('');
    setShowUploadVersionModal(true);
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Published':
      case 'Approved':
        return <FaCheckCircle className="text-success" />;
      case 'Under Review':
        return <FaClock className="text-warning" />;
      case 'Draft':
        return <FaEdit className="text-info" />;
      case 'Expired':
        return <FaExclamationCircle className="text-danger" />;
      default:
        return <FaBan className="text-secondary" />;
    }
  };

  const getStatusBadgeClass = (status: DocumentStatus) => {
    switch (status) {
      case 'Published':
      case 'Approved':
        return 'badge bg-success';
      case 'Under Review':
        return 'badge bg-warning text-dark';
      case 'Draft':
        return 'badge bg-info';
      case 'Expired':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={32} />;
    } else if (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc')) {
      return <FaFileWord className="text-primary" size={32} />;
    }
    return <MdOutlineDescription size={32} />;
  };

  const isExpired = (expiryDate?: Date | string): boolean => {
    if (!expiryDate) return false;
    // Convert to Date if it's a string
    const date = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    if (isNaN(date.getTime())) return false; // Invalid date
    return date < new Date();
  };

  const isExpiringSoon = (expiryDate?: Date | string): boolean => {
    if (!expiryDate) return false;
    // Convert to Date if it's a string
    const date = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    if (isNaN(date.getTime())) return false; // Invalid date
    const daysUntilExpiry = Math.floor(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  const handleDownload = async (versionId?: string) => {
    if (!document) return;
    try {
      setProcessing(true);
      const blob = await documentService.downloadDocument(document.metadata.id, versionId);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      // Get filename from version or use document title
      const fileName = versionId 
        ? document.versions.find(v => v.id === versionId)?.fileName || `document-${document.metadata.id}.pdf`
        : currentVersion?.fileName || `document-${document.metadata.id}.pdf`;
      
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download document');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="document-management-container">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="document-management-container">
        <div className="container-fluid p-4" style={{ zoom: 0.9 }}>
          <div className="alert alert-danger">Document not found</div>
        </div>
      </div>
    );
  }

  const currentVersion = document.versions.find(
    v => v.versionNumber === document.metadata.currentVersion
  );
  const isCurrentUserOwner = currentUserId && document.metadata.documentOwner === currentUserId;

  return (
    <div className="document-management-container">
      <div className="container-fluid p-4" style={{ paddingTop: '2rem', marginTop: '100px', zoom: 0.9 }}>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
          <span className="mx-2">/</span>
          <BreadcrumbItem>
            <button
              className="btn btn-link text-decoration-none p-0"
              onClick={() => navigate('/documents')}
              style={{ color: 'inherit' }}
            >
              Document Management
            </button>
          </BreadcrumbItem>
          <span className="mx-2">/</span>
          <BreadcrumbItem active style={{ paddingRight: '10px' }}>
            {document.metadata.title}
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            {getFileIcon(currentVersion?.fileName || '')}
            <div>
              <h5 className="mb-0">{document.metadata.title}</h5>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className="small" style={{ color: '#495057' }}>Version {document.metadata.currentVersion}</span>
                <span className={getStatusBadgeClass(document.metadata.status)}>
                  {document.metadata.status}
                </span>
                {getStatusIcon(document.metadata.status)}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2">
              {document.metadata.status === 'Draft' && isCurrentUserOwner && (
                <>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigate(`/documents/${document.metadata.id}/edit`)}
                    disabled={processing}
                  >
                    <FaEdit className="me-2" />
                    Edit
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitForReview}
                    disabled={processing}
                  >
                    Submit for Review
                  </button>
                </>
              )}
              {document.metadata.status === 'Draft' && !isCurrentUserOwner && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate(`/documents/${document.metadata.id}/edit`)}
                  disabled={processing}
                >
                  <FaEdit className="me-2" />
                  Edit
                </button>
              )}
              {document.metadata.status === 'Under Review' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => openReviewModal(true)}
                    disabled={processing}
                  >
                    Approve Review
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => openApproveModal('approve')}
                    disabled={processing}
                  >
                    Approve as Final
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => openReviewModal(false)}
                    disabled={processing}
                  >
                    Request Changes
                  </button>
                </>
              )}
              {document.metadata.status === 'Approved' && (
                <button
                  className="btn btn-primary"
                  onClick={() => openApproveModal('publish')}
                  disabled={processing}
                >
                  Publish
                </button>
              )}
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'versions' ? 'active' : ''}`}
              onClick={() => setActiveTab('versions')}
            >
              <FaHistory className="me-2" />
              Version History ({document.versions.length})
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'approval' ? 'active' : ''}`}
              onClick={() => setActiveTab('approval')}
            >
              <FaCheckCircle className="me-2" />
              Approval Workflow
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Document Information</h5>
                </div>
                <div className="card-body">
                  {document.metadata.description && (
                    <div className="mb-4">
                      <h6>Description</h6>
                      <p style={{ color: '#495057' }}>{document.metadata.description}</p>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Category:</strong>
                      <div>
                        <span className="badge bg-secondary">{document.metadata.category}</span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <strong>Framework:</strong>
                      <div>
                        {document.metadata.framework ? (
                          <span className="badge bg-primary">{document.metadata.framework}</span>
                        ) : (
                          <span style={{ color: '#495057' }}>Not specified</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {document.metadata.controlId && (
                    <div className="mb-3">
                      <strong>Control ID:</strong>
                      <div>
                        <span className="badge bg-info">{document.metadata.controlId}</span>
                      </div>
                    </div>
                  )}

                  {document.metadata.folderId && folderName && (
                    <div className="mb-3">
                      <strong>Folder:</strong>
                      <div>
                        <span className="badge bg-secondary">{folderName}</span>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Risk Level:</strong>
                      <div>
                        {document.metadata.riskLevel ? (
                          <span
                            className={`risk-level-badge ${document.metadata.riskLevel.toLowerCase()}`}
                          >
                            {document.metadata.riskLevel}
                          </span>
                        ) : (
                          <span style={{ color: '#495057' }}>Not specified</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <strong>Document Owner:</strong>
                      <div>{document.metadata.documentOwnerName}</div>
                    </div>
                  </div>

                  {document.metadata.expiryDate && (
                    <div className="mb-3">
                      <strong>Expiry Date:</strong>
                      <div
                        className={
                          isExpired(document.metadata.expiryDate)
                            ? 'text-danger fw-bold'
                            : isExpiringSoon(document.metadata.expiryDate)
                            ? 'text-warning fw-bold'
                            : ''
                        }
                      >
                        {formatDate(document.metadata.expiryDate)}
                        {isExpired(document.metadata.expiryDate) && ' (Expired)'}
                        {isExpiringSoon(document.metadata.expiryDate) &&
                          !isExpired(document.metadata.expiryDate) &&
                          ' (Expiring Soon)'}
                      </div>
                    </div>
                  )}

                  {document.metadata.tags && document.metadata.tags.length > 0 && (
                    <div className="mb-3">
                      <strong>Tags:</strong>
                      <div>
                        {document.metadata.tags.map((tag, idx) => (
                          <span key={idx} className="badge bg-light text-dark me-1">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <strong>Created:</strong>
                      <div style={{ color: '#495057' }}>{formatDate(document.metadata.createdAt)}</div>
                    </div>
                    <div className="col-md-6">
                      <strong>Last Updated:</strong>
                      <div style={{ color: '#495057' }}>{formatDate(document.metadata.updatedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Current Version</h5>
                </div>
                <div className="card-body">
                  {currentVersion && (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        {getFileIcon(currentVersion.fileName)}
                        <div>
                          <div className="fw-bold">{currentVersion.fileName}</div>
                          <div className="small" style={{ color: '#495057' }}>
                            {formatFileSize(currentVersion.fileSize)}
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Uploaded by:</strong>
                        <div>{currentVersion.uploadedByName}</div>
                        <div className="small" style={{ color: '#495057' }}>
                          {formatDate(currentVersion.uploadedAt)}
                        </div>
                      </div>
                      {currentVersion.changeDescription && (
                        <div className="mb-3">
                          <strong>Change Description:</strong>
                          <div className="small" style={{ color: '#495057' }}>
                            {currentVersion.changeDescription}
                          </div>
                        </div>
                      )}
                      <button 
                        className="btn btn-outline-primary w-100"
                        onClick={() => handleDownload()}
                        disabled={processing}
                      >
                        <FaDownload className="me-2" />
                        Download
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'versions' && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Version History</h5>
                {isCurrentUserOwner && (
                  <button
                    className="btn btn-primary"
                    onClick={openUploadVersionModal}
                    disabled={processing}
                  >
                    <FaUpload className="me-2" />
                    Upload New Version
                  </button>
                )}
              </div>
              {(!document.versions || document.versions.length === 0) ? (
                <div className="text-center p-4" style={{ color: '#495057' }}>
                  <p>No version history available.</p>
                </div>
              ) : (
                document.versions
                .sort((a, b) => {
                  // Sort by version number (descending)
                  return b.versionNumber - a.versionNumber;
                })
                .map((version, idx) => {
                  const isCurrent = version.versionNumber === document.metadata.currentVersion;
                  return (
                    <div
                      key={version.id}
                      className={`version-history-item ${isCurrent ? 'current' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <h6 className="mb-0">Version {version.versionNumber}</h6>
                            {isCurrent && (
                              <span className="badge bg-primary">Current</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            {getFileIcon(version.fileName)}
                            <div>
                              <div>{version.fileName}</div>
                              <div className="small" style={{ color: '#495057' }}>{formatFileSize(version.fileSize)}</div>
                            </div>
                          </div>
                          {version.changeDescription && (
                            <div className="mb-2">
                              <strong>Changes:</strong>
                              <div style={{ color: '#495057' }}>{version.changeDescription}</div>
                            </div>
                          )}
                          <div className="small" style={{ color: '#495057' }}>
                            <div>Uploaded by {version.uploadedByName}</div>
                            <div>{formatDate(version.uploadedAt)}</div>
                            {version.modifiedAt && (
                              <div>Modified: {formatDate(version.modifiedAt)}</div>
                            )}
                          </div>
                        </div>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleDownload(version.id)}
                          disabled={processing}
                        >
                          <FaDownload /> Download
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'approval' && (
          <div className="card">
            <div className="card-body">
              <h5 className="mb-4">Approval Workflow</h5>
              
              {/* Workflow Timeline - Show Draft first, then each approval record as a row */}
              <div className="workflow-timeline">
                {/* Draft Row - Initial State */}
                <div className={`workflow-step ${document.metadata.status === 'Draft' && (!document.approvals || document.approvals.length === 0) ? 'active' : 'completed'}`}>
                  <div className="step-indicator">
                    <div className="step-icon">
                      {document.metadata.status === 'Draft' && (!document.approvals || document.approvals.length === 0) ? '📝' : '✅'}
                    </div>
                    <div className="step-line"></div>
                  </div>
                  <div className="step-content">
                    <div className="step-header">
                      <h6 className="mb-1">Draft</h6>
                      <span className={getStatusBadgeClass('Draft')}>Draft</span>
                    </div>
                    <div className="step-details">
                      <div className="small" style={{ color: '#495057' }}>
                        <div>Created by: {document.metadata.documentOwnerName}</div>
                        <div>Created: {formatDate(document.metadata.createdAt)}</div>
                        {document.metadata.status === 'Draft' && (!document.approvals || document.approvals.length === 0) && (
                          <div className="mt-2">
                            <span className="badge bg-info">Current Status</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Each Approval Record as a Separate Row */}
                {document.approvals && document.approvals.length > 0 ? (
                  document.approvals
                    .sort((a, b) => a.submittedAt.getTime() - b.submittedAt.getTime()) // Sort chronologically
                    .map((approval, idx) => {
                      // Find the current approval (the most recent one that matches document status)
                      const isCurrent = approval.status === document.metadata.status && 
                        idx === document.approvals.length - 1;
                      const isRefused = approval.reviewComments && 
                        approval.reviewComments.toLowerCase().includes('reject') &&
                        (approval.status === 'Draft' || !approval.approvedBy);
                      const isUnderReview = approval.status === 'Under Review';
                      const isApproved = approval.status === 'Approved' && approval.approvedBy;
                      const isPublished = approval.status === 'Published' && approval.publishedBy;
                      const wasReturned = approval.reviewComments && 
                        approval.reviewComments.toLowerCase().includes('return') &&
                        document.metadata.status === 'Draft';

                      // Determine step type and icon
                      let stepTitle = '';
                      let stepIcon = '';
                      let stepClass = '';

                      if (isPublished) {
                        stepTitle = 'Published';
                        stepIcon = '📢';
                        stepClass = isCurrent ? 'active' : 'completed';
                      } else if (isApproved) {
                        stepTitle = 'Approved';
                        stepIcon = '✅';
                        stepClass = isCurrent ? 'active' : 'completed';
                      } else if (isRefused || wasReturned) {
                        stepTitle = 'Refused / Returned to Draft';
                        stepIcon = '❌';
                        stepClass = 'refused';
                      } else if (isUnderReview) {
                        stepTitle = 'Under Review';
                        stepIcon = '👀';
                        stepClass = isCurrent ? 'active' : 'pending';
                      } else {
                        stepTitle = approval.status;
                        stepIcon = '⏳';
                        stepClass = 'pending';
                      }

                      return (
                        <div 
                          key={approval.id} 
                          className={`workflow-step ${stepClass}`}
                        >
                          <div className="step-indicator">
                            <div className="step-icon">
                              {stepIcon}
                            </div>
                            {idx < document.approvals.length - 1 && <div className="step-line"></div>}
                          </div>
                          <div className="step-content">
                            <div className="step-header">
                              <h6 className="mb-1">
                                {stepTitle}
                                {isCurrent && (
                                  <span className="badge bg-primary ms-2">Current</span>
                                )}
                              </h6>
                              <span className={getStatusBadgeClass(approval.status)}>
                                {approval.status}
                              </span>
                            </div>
                            <div className="step-details">
                              <div className="small" style={{ color: '#495057' }}>
                                {/* Show submitted info for all records */}
                                <div><strong>Submitted for Review:</strong> {formatDate(approval.submittedAt)} by {approval.submittedByName}</div>
                                
                                {/* Show reviewed info if this record has review action */}
                                {approval.reviewedBy && approval.reviewedAt && (
                                  <div className="mt-1">
                                    <strong>Reviewed:</strong> {formatDate(approval.reviewedAt)} by {approval.reviewedByName}
                                  </div>
                                )}

                                {/* Show approved info if this record has approval action */}
                                {approval.approvedBy && approval.approvedAt && (
                                  <div className="mt-1">
                                    <strong>Approved:</strong> {formatDate(approval.approvedAt)} by {approval.approvedByName}
                                  </div>
                                )}

                                {/* Show published info if this record has publish action */}
                                {approval.publishedBy && approval.publishedAt && (
                                  <div className="mt-1">
                                    <strong>Published:</strong> {formatDate(approval.publishedAt)} by {approval.publishedByName}
                                  </div>
                                )}
                              </div>

                              {/* Show review comments if available */}
                              {approval.reviewComments && (
                                <div className="alert alert-info mt-2 mb-0">
                                  <strong>Review Comments:</strong> {approval.reviewComments}
                                </div>
                              )}

                              {/* Show approval comments if available */}
                              {approval.approvalComments && (
                                <div className="alert alert-success mt-2 mb-0">
                                  <strong>Approval Comments:</strong> {approval.approvalComments}
                                </div>
                              )}

                              {/* Show rejection/return message if refused/returned */}
                              {(isRefused || wasReturned) && approval.reviewComments && (
                                <div className={`alert ${isRefused ? 'alert-danger' : 'alert-warning'} mt-2 mb-0`}>
                                  <strong>{isRefused ? 'Refused:' : 'Returned to Draft:'}</strong> {approval.reviewComments}
                                </div>
                              )}

                              {/* Show current status badge */}
                              {isCurrent && (
                                <div className="mt-2">
                                  <span className="badge bg-primary">Current Status</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center p-4" style={{ color: '#495057' }}>
                    <p>No approval records yet. Submit the document for review to start the workflow.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        <Modal isOpen={showReviewModal} toggle={() => setShowReviewModal(false)}>
          <ModalHeader toggle={() => setShowReviewModal(false)}>
            {reviewApproved ? 'Approve Review' : 'Request Changes'}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">
                {reviewApproved 
                  ? 'Add comments (optional)' 
                  : 'Please provide reason for requesting changes'}
              </label>
              <textarea
                className="form-control"
                rows={4}
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder={reviewApproved 
                  ? 'Add any comments about this review...' 
                  : 'Explain what changes are needed...'}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowReviewModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              color={reviewApproved ? 'success' : 'warning'} 
              onClick={() => handleReview(reviewApproved, reviewComments || undefined)}
              disabled={processing || (!reviewApproved && !reviewComments.trim())}
            >
              {processing ? 'Processing...' : (reviewApproved ? 'Approve Review' : 'Request Changes')}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Approve/Publish Modal */}
        <Modal isOpen={showApproveModal} toggle={() => setShowApproveModal(false)}>
          <ModalHeader toggle={() => setShowApproveModal(false)}>
            {approveModalAction === 'approve' ? 'Approve Document' : 'Publish Document'}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <p>
                {approveModalAction === 'approve' 
                  ? 'Are you sure you want to approve this document as final? Once approved, it can be published.'
                  : 'Are you sure you want to publish this document? Once published, it will be available to all users.'}
              </p>
              <label className="form-label">Add comments (optional)</label>
              <textarea
                className="form-control"
                rows={3}
                value={approveComments}
                onChange={(e) => setApproveComments(e.target.value)}
                placeholder={approveModalAction === 'approve' 
                  ? 'Add any comments about approving this document...'
                  : 'Add any comments about publishing this document...'}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowApproveModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onClick={() => approveModalAction === 'approve' ? handleApprove(approveComments || undefined) : handlePublish()}
              disabled={processing}
            >
              {processing 
                ? (approveModalAction === 'approve' ? 'Approving...' : 'Publishing...') 
                : (approveModalAction === 'approve' ? 'Approve Document' : 'Publish Document')}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Upload New Version Modal */}
        <Modal isOpen={showUploadVersionModal} toggle={() => setShowUploadVersionModal(false)}>
          <ModalHeader toggle={() => setShowUploadVersionModal(false)}>
            Upload New Version
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">Select File *</label>
              <input
                type="file"
                className="form-control"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setVersionFile(file);
                }}
                disabled={processing}
              />
              {versionFile && (
                <div className="mt-2 small" style={{ color: '#495057' }}>
                  Selected: {versionFile.name} ({(versionFile.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Change Description (optional)</label>
              <textarea
                className="form-control"
                rows={4}
                value={versionChangeDescription}
                onChange={(e) => setVersionChangeDescription(e.target.value)}
                placeholder="Describe what changed in this version..."
                disabled={processing}
              />
            </div>
            <div className="alert alert-warning">
              <small>
                <strong>Note:</strong> Uploading a new version will create version {document ? document.metadata.currentVersion + 1 : 'N/A'} and reset the document status to <strong>Draft</strong>. The document will need to go through the approval workflow again.
              </small>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowUploadVersionModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onClick={handleUploadVersion}
              disabled={processing || !versionFile}
            >
              {processing ? 'Uploading...' : 'Upload Version'}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default DocumentDetails;

